/**
 * Which Marketplace apps a practice has, and which of them are ready — shared
 * by the Marketplace dashboard and the Marketplace Orders page so the two can
 * never disagree about an app or an order.
 *
 * An app is SWITCHED ON by the sidebar's rule (filterNavItemsByModuleAccess
 * over the catalogue's module gates), and READY once it is also set up, judged
 * by the same endpoints as its setup screen — see marketplaceCatalog.ts. A
 * switched-on app that is not ready shows on the dashboard with Connect.
 */
import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  getEnabledIntegrations,
  getIntegrationOnboarding,
  type EnabledIntegration,
  type IntegrationOrder,
} from '@/services/integrations.service';
import { useModuleAccessMap } from '@/hooks/useNavAccess';
import { filterNavItemsByModuleAccess } from '@/components/layout/navigation.config';
import { useFacilityOverrideValues } from '@/pages/dashboard/dashboardFilters';
import { linkedOrderSearch } from '@/pages/patients/labs/linkedOrder';
import { NO_FACILITY } from './integrationOrdersShared';
import { MARKETPLACE_APPS, MARKETPLACE_ORDERS_PATH, asNavItems, type MarketplaceApp } from './marketplaceCatalog';

export { MARKETPLACE_ORDERS_PATH };

/** Order numbers, the order pages and the main buttons: the level the app pages need (Peptides' sidebar minUserLevel). */
export const ORDERS_LEVEL = 2;

/** The apps whose module is on for this account — decided by the sidebar's filter. */
export function enabledApps(access: Parameters<typeof filterNavItemsByModuleAccess>[1]): MarketplaceApp[] {
  const visible = new Set(filterNavItemsByModuleAccess(asNavItems(MARKETPLACE_APPS), access).map((i) => i.label));
  return MARKETPLACE_APPS.filter((app) => visible.has(app.id));
}

/**
 * Of the switched-on apps, those set up for this account. `integrations` is
 * GET /integrations/enabled, or null when it could not be read; `onboarded`
 * maps an integration key to its onboarding answer, missing while unknown.
 * An answer we could not get counts as set up: a failed read should not turn
 * an app that may well be working into a Connect button.
 */
export function setUpApps(
  apps: MarketplaceApp[],
  integrations: EnabledIntegration[] | null,
  onboarded: Record<string, boolean>,
): MarketplaceApp[] {
  return apps.filter((app) => {
    if (!app.integrationKey) return true;
    if (integrations) {
      const live = integrations.find((i) => i.key === app.integrationKey);
      if (!live || live.products.length === 0) return false;
    }
    return !app.requiresOnboarding || onboarded[app.integrationKey] !== false;
  });
}

/** The switched-on apps and the ready ones among them, with the set-up answers loading. */
export function useMarketplaceApps(): { switchedOn: MarketplaceApp[]; ready: MarketplaceApp[]; checking: boolean } {
  const access = useModuleAccessMap();
  const switchedOn = useMemo(() => enabledApps(access), [access]);

  // Same query keys as the setup screens, so a change there shows here at once.
  const integrationsQuery = useQuery({
    queryKey: ['integrations', 'enabled'],
    queryFn: getEnabledIntegrations,
    staleTime: 5 * 60_000,
  });
  const onboardingKeys = switchedOn
    .filter((app) => app.requiresOnboarding && app.integrationKey)
    .map((app) => app.integrationKey as string);
  const onboardingQueries = useQueries({
    queries: onboardingKeys.map((key) => ({
      queryKey: ['integration-onboarding', key],
      queryFn: () => getIntegrationOnboarding(key),
    })),
  });
  const onboarded: Record<string, boolean> = {};
  onboardingKeys.forEach((key, i) => {
    const data = onboardingQueries[i]?.data;
    if (data) onboarded[key] = data.onboarded;
  });
  const checking = integrationsQuery.isLoading || onboardingQueries.some((q) => q.isLoading);
  // `onboarded` is rebuilt every render; its contents are what the memo keys on.
  const onboardedKey = JSON.stringify(onboarded);
  const ready = useMemo(
    () => setUpApps(switchedOn, integrationsQuery.data ?? null, JSON.parse(onboardedKey) as Record<string, boolean>),
    [switchedOn, integrationsQuery.data, onboardedKey],
  );
  return { switchedOn, ready, checking };
}

/** Each vendor key an order can carry, mapped to the app it belongs to. */
export function appsByVendorKey(apps: MarketplaceApp[]): Map<string, MarketplaceApp> {
  const appByKey = new Map<string, MarketplaceApp>();
  for (const app of apps) {
    for (const key of app.orderKeys ?? (app.integrationKey ? [app.integrationKey] : [])) appByKey.set(key, app);
  }
  return appByKey;
}

/** Cancelled by staff or by the vendor — the Peptides page's rule (peptideOrder.ts). */
export function isOrderCancelled(o: IntegrationOrder): boolean {
  return o.fulfillmentStatus === 'cancelled' || o.status === 'cancelled';
}

/** When it was cancelled, if staff cancelled it in Prospera (the vendor's cancellations carry no date). */
export function orderCancelledAt(o: IntegrationOrder): string | null {
  const at = o.metadata?.cancelledAt;
  return typeof at === 'string' ? at : null;
}

/**
 * The order itself in the patient's chart, by patient ID (never by name — two
 * patients can share one): its section, with its details open (linkedOrder.ts).
 * Null when the order has no patient or its app has no chart section.
 */
export function orderChartHref(app: MarketplaceApp | undefined, o: IntegrationOrder): string | null {
  if (!app?.chartSection || !o.patientId) return null;
  const base = `/patients/${o.patientId}?section=${app.chartSection}`;
  return o.vendorKey ? `${base}&${linkedOrderSearch({ id: o.integrationOrderId, vendorKey: o.vendorKey })}` : base;
}

/**
 * The facility scope the order pages use: under the sidebar's facility
 * selection, that facility plus orders with no facility; otherwise everything.
 */
export function useOrderScope(): (o: IntegrationOrder) => boolean {
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(NO_FILTERS);
  return useMemo(() => {
    const ids = isFacilityLocked && Array.isArray(effectiveValues.facility)
      ? [...(effectiveValues.facility as string[]), NO_FACILITY]
      : null;
    return (o: IntegrationOrder) =>
      !ids || ids.includes(o.facilityId == null || o.facilityId === 0 ? NO_FACILITY : String(o.facilityId));
  }, [isFacilityLocked, effectiveValues]);
}

const NO_FILTERS = {};

/** The preset each summary tile opens the Marketplace Orders page on. */
export type MarketplaceOrdersView = 'all' | 'month' | 'cancelled' | 'awaiting';
export const MARKETPLACE_ORDERS_VIEWS: readonly MarketplaceOrdersView[] = ['all', 'month', 'cancelled', 'awaiting'];

export function marketplaceOrdersHref(view: MarketplaceOrdersView): string {
  return `${MARKETPLACE_ORDERS_PATH}?show=${view}`;
}
